import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { apiClient } from '@/api/client'
import { useTeam } from '@/hooks/useTeam'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { StatsCharts } from '@/components/stats/StatsCharts'
import { 
  ArrowLeft, 
  User, 
  Trophy,
  TrendingUp,
  Target,
  Zap,
  Users,
  Mail
} from 'lucide-react'

interface PlayerStats {
  user_id: string
  display_name: string
  email: string
  profile_picture_url: string | null
  total_games: number
  wins: number
  losses: number
  win_rate: number
  total_points_scored: number
  total_points_conceded: number
  goals_by_type: Record<string, number>
  goals_by_type_array?: Array<{ goal_type: string; count: number }>
  role_breakdown: {
    defense: number
    attack: number
    full: number
  }
  role_breakdown_array?: Array<{ role: string; games: number; wins: number }>
  performance_over_time?: Array<{ date: string; result: string; points: number }>
  recent_games: Array<{
    id: string
    date: string
    game_type: string
    final_red_score: number
    final_blue_score: number
    player_team: 'red' | 'blue'
    result: 'win' | 'loss' | 'tie'
  }>
}

export function PlayerProfile() {
  const { id } = useParams()
  const { getAccessTokenSilently } = useAuth0()
  const { currentTeam } = useTeam()

  const { data: playerStats, isLoading } = useQuery({
    queryKey: ['player-stats', id, currentTeam?.id],
    queryFn: async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.get(`/stats/player/${id}?team_id=${currentTeam?.id}`)
      return response.data as PlayerStats
    },
    enabled: !!id && !!currentTeam?.id,
  })

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getGoalTypeLabel = (goalType: string) => {
    const labels: Record<string, string> = {
      'attack_shot': 'Attack',
      'midfield_shot': 'Midfield',
      'defense_shot': 'Defense',
      'goalkeeper_shot': 'Goalkeeper',
      'own_goal_attack': 'Own Goal (Attack)',
      'own_goal_midfield': 'Own Goal (Midfield)',
      'own_goal_defense': 'Own Goal (Defense)',
      'own_goal_goalkeeper': 'Own Goal (Goalkeeper)',
      'monsterblock': 'Monsterblock',
      'gamelle': 'Gamelle'
    }
    return labels[goalType] || goalType
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!playerStats) {
    return (
      <div className="space-y-6">
        <Link
          to="/players"
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Players
        </Link>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900">Player not found</h3>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/players"
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Players
        </Link>
      </div>

      {/* Player Info Card */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-8 bg-gradient-to-r from-blue-500 to-blue-600">
          <div className="flex items-center space-x-6">
            {playerStats.profile_picture_url ? (
              <img 
                src={playerStats.profile_picture_url} 
                alt={playerStats.display_name}
                className="h-24 w-24 rounded-full border-4 border-white object-cover"
              />
            ) : (
              <div className="h-24 w-24 rounded-full border-4 border-white bg-white flex items-center justify-center">
                <User className="h-12 w-12 text-blue-500" />
              </div>
            )}
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white">{playerStats.display_name}</h1>
              <div className="flex items-center mt-2 text-blue-100">
                <Users className="h-4 w-4 mr-2" />
                <span className="text-sm">Team: {currentTeam?.name}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <Target className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Games</p>
              <p className="text-2xl font-bold text-gray-900">{playerStats.total_games}</p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <Trophy className="h-8 w-8 text-yellow-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Wins</p>
              <p className="text-2xl font-bold text-gray-900">{playerStats.wins}</p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Win Rate</p>
              <p className="text-2xl font-bold text-gray-900">{(playerStats.win_rate * 100).toFixed(1)}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <Zap className="h-8 w-8 text-orange-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Individual Points</p>
              <p className="text-2xl font-bold text-gray-900">{playerStats.total_points_scored}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Role Breakdown */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Role Distribution</h3>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-4">
              {playerStats.role_breakdown.defense > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">Defense</span>
                    <span className="text-sm text-gray-600">{playerStats.role_breakdown.defense} games</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-red-500 h-2 rounded-full" 
                      style={{ width: `${(playerStats.role_breakdown.defense / playerStats.total_games) * 100}%` }}
                    />
                  </div>
                </div>
              )}
              {playerStats.role_breakdown.attack > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">Attack</span>
                    <span className="text-sm text-gray-600">{playerStats.role_breakdown.attack} games</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full" 
                      style={{ width: `${(playerStats.role_breakdown.attack / playerStats.total_games) * 100}%` }}
                    />
                  </div>
                </div>
              )}
              {playerStats.role_breakdown.full > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">Full Side (1v1)</span>
                    <span className="text-sm text-gray-600">{playerStats.role_breakdown.full} games</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full" 
                      style={{ width: `${(playerStats.role_breakdown.full / playerStats.total_games) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Goals by Type */}
        {Object.keys(playerStats.goals_by_type).length > 0 && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Goals by Type</h3>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-2">
                {Object.entries(playerStats.goals_by_type)
                  .sort(([, a], [, b]) => b - a)
                  .map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{getGoalTypeLabel(type)}</span>
                      <span className="font-medium text-gray-900">{count}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Statistics Charts */}
      <StatsCharts 
        goalsByType={playerStats.goals_by_type_array}
        performanceOverTime={playerStats.performance_over_time}
        roleBreakdown={playerStats.role_breakdown_array}
      />

      {/* Recent Games */}
      {playerStats.recent_games && playerStats.recent_games.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Games</h3>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-3">
              {playerStats.recent_games.map((game) => (
                <Link
                  key={game.id}
                  to={`/games/${game.id}`}
                  className="block border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        game.result === 'win' 
                          ? 'bg-green-100 text-green-800' 
                          : game.result === 'loss'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {game.result.toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-600">
                        {game.game_type === '1v1' ? '1v1' : '2v2'}
                      </span>
                      <span className="text-sm text-gray-500">{formatDate(game.date)}</span>
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {game.final_red_score} - {game.final_blue_score}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

